import {
  DiscordIcon,
  ExternalLinkIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  SmileFaceIcon,
  TelegramIcon,
  TwitterIcon,
  WebsiteIcon,
} from "@assets";
import { Box, List, ListItem, Text, useColorModeValue } from "@chakra-ui/react";
import { NextImage } from "@components/NextImage/NextImage";
import { EventType, NFTEvent, NFTEventInfo } from "@models/event";
import { compareText, toDuration, unique } from "@models/utils";
import { isEmpty } from "lodash";
import { CalendarGradientIcon, LocationGradientIcon } from "public";
import { BiUserCheck } from "react-icons/bi";
import EventDetailMap from "./EventDetailMap";
import ImageWindow from "./ImageWindow";

const GOOGLE_URL = "https://www.google.com/maps";

const ListItemWrapper = ({ imgSrc, imgAlt, children, align }: any) => (
  <ListItem
    display="flex"
    flexDir="row"
    alignItems={align ? align : "center"}
    justifyContent="flex-start"
    gap={"10px"}
    className="text-primary"
  >
    <NextImage className="w-8 svg-custom" src={imgSrc} alt={imgAlt} />
    <Box fontWeight={500} fontSize="1rem" textAlign="left" color="neutral.300">
      {children}
    </Box>
  </ListItem>
);

const EventTime = ({
  event,
  info,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
}) => {
  const isBenefitEvent = compareText(event.type, EventType.BENEFIT);

  let ticketDateTime =
    info.date || info.time
      ? unique([info.date, info.time]).join(", ")
      : event.duration;

  if (event && !isEmpty(event.tiers)) {
    const tier = event.tiers[0];
    ticketDateTime =
      !isEmpty(tier.paymentConfig) && isBenefitEvent
        ? toDuration(
            tier.paymentConfig.start,
            tier.paymentConfig.end,
            event.timezone
          )
        : ticketDateTime;
  }

  return (
    <div className="flex flex-row gap-3">
      <div className="bg-white bg-opacity-40 h-fit rounded dark:bg-transparent">
        <NextImage className="w-8" src={CalendarGradientIcon} alt="date" />
      </div>
      <div className="flex flex-col">
        <span className="text-black dark:text-white font-bold">
          Public Sale Date
        </span>
        <Text color="rgb(20,101,136)">{ticketDateTime}</Text>
      </div>
    </div>
  );
};

const EventLocation = ({ info }: { info: NFTEventInfo }) => (
  <div className="flex flex-row gap-3">
    <div className="bg-white bg-opacity-40 h-fit rounded dark:bg-transparent">
      <NextImage className="w-8" src={LocationGradientIcon} alt="location" />
    </div>
    <div className="flex flex-col">
      <span className="text-[#000000] dark:text-white font-bold">
        Asset Location
      </span>
      {info.locationUrl ? (
        <a
          href={info.locationUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
        >
          <Text textDecoration="underline" color={"rgb(20,101,136)"}>
            {info.location}
          </Text>
        </a>
      ) : (
        <Text color={"rgb(20,101,136)"}>{info.location}</Text>
      )}
    </div>
  </div>
);

const EventHost = ({ info }: { info: NFTEventInfo; eventType: EventType }) => (
  <ListItemWrapper imgSrc={SmileFaceIcon} imgAlt="simle-face icon">
    <Text color={useColorModeValue("#000000", "")} className="font-bold">
      Powered by&nbsp;
      {info.organizer.url ? (
        <a href={info.organizer.url} target="_blank" rel="noreferrer">
          <span className="text-primary underline">{info.organizer.name}</span>
        </a>
      ) : (
        info.organizer.name
      )}
    </Text>
  </ListItemWrapper>
);

const EventEligiblity = () => (
  <ListItem
    display="flex"
    flexDir="row"
    alignItems="center"
    justifyContent="flex-start"
    gap={"10px"}
    className="text-primary"
  >
    <BiUserCheck size={24} className="w-8" style={{ paddingLeft: "0.3rem" }} />
    <Text color={useColorModeValue("#000000", "")} className="font-bold">
      Open for Public
    </Text>
  </ListItem>
);

const EventExternalLinks = ({ info }: { info: NFTEventInfo }) => {
  const externalLinkIcons = {
    twitter: TwitterIcon,
    discord: DiscordIcon,
    linkedin: LinkedInIcon,
    instagram: InstagramIcon,
    telegram: TelegramIcon,
    facebook: FacebookIcon,
    website: WebsiteIcon,
  };
  const hasIcon = (name) => {
    return Object.keys(externalLinkIcons).includes(name);
  };
  const linksWithIcon =
    info.externalLinks && info.externalLinks.length > 0
      ? info.externalLinks.filter((link) => hasIcon(link.name.toLowerCase()))
      : [];
  const linksWithText =
    info.externalLinks && info.externalLinks.length > 0
      ? info.externalLinks.filter((link) => !hasIcon(link.name.toLowerCase()))
      : [];

  return (
    <ListItemWrapper imgSrc={ExternalLinkIcon} imgAlt="external-link icon">
      <div className="flex flex-row flex-wrap items-center">
        {linksWithIcon.length > 0 &&
          linksWithIcon.map((link, index) => {
            return (
              <a
                className="mr-5 text-neutral dark:text-white"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                key={index}
              >
                <NextImage
                  className="w-5"
                  src={externalLinkIcons[link.name.toLowerCase()]}
                />
              </a>
            );
          })}
        {linksWithText.length > 0 &&
          linksWithText.map((link, index) => {
            return (
              <a
                className="mr-5 text-neutral dark:text-white"
                href={link.url}
                target="_blank"
                rel="noreferrer"
                key={index}
              >
                <Text
                  textDecoration="underline"
                  color={"primary.500"}
                  textTransform="capitalize"
                >
                  {link.name}
                </Text>
              </a>
            );
          })}
      </div>
    </ListItemWrapper>
  );
};

const EventDetailInfo = ({
  event,
  info,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
}) => {
  return (
    <div className="mb-16 w-full ">
      <List spacing={3}>
        <div
          className={`flex flex-col md:flex-row gap-6 mb-4 bg-primary-50 backdrop-blur rounded-lg p-4`}
          style={{
            borderRadius: "12px",
            background:
              "linear-gradient(219deg, rgba(20, 160, 233, 0.45) 0%, rgba(22, 247, 232, 0.45) 100%)",
          }}
        >
          <EventTime event={event} info={info} />

          {info.location && <EventLocation info={info} />}
        </div>

        <EventDetailMap info={info} />
        <ImageWindow />
        {info.organizer && info.organizer.name && (
          <EventHost info={info} eventType={event.type} />
        )}

        {info.organizer && info.organizer.name && <EventEligiblity />}

        {info.externalLinks && info.externalLinks.length > 0 && (
          <EventExternalLinks info={info} />
        )}
      </List>
    </div>
  );
};

export default EventDetailInfo;
